import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {DNA_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import * as BiojsUtil from 'biojs/Util';
import {Strand} from 'constants/MolbioConstants';
import {MatchSite} from 'constants/SequenceConstants';
import {
  GC_LEVEL_NAME,
  GCLevel,
  HairpinParameter,
  MAXIMUM_PATTERN_LENGTH,
  OptimizationMetrics,
  Organism,
} from 'molbio/codon-optimization/CodonOptimizationConstants';
import {
  EnzymeRecognitionSite,
  PatternToReduce,
  ProtectedRegion,
} from 'molbio/codon-optimization/reduceCodonOptimizationTab';
import {APIEnzymeRecognitionSite} from 'molbio/dao/CodonOptimizationDAO';
import * as sequtil from 'molbio/SeqUtil';
import {getLetterCase, withCase} from 'util/CapitalizationUtil';
import {NumericRange} from 'util/geometry/GeometryUtil';
import sortedBy from 'util/sortedBy';

type MetricKeys = 'numRareCodons' | 'gcContent' | 'thymineContent' | 'numHairpins';

// Exported to avoid TS warning that the value is never read.
// If this fails typechecking, it means MetricKeys is missing a type! Please fill it in!
// e.g. "Property 'numRareCodons' is missing in type..." means MetricKeys is missing numRareCodons
export const __checkTypes: Omit<OptimizationMetrics, MetricKeys | 'numCodonsChanged' | 'patterns'> = {};

export function doesAnnotationOverlapRegion(
  annotationStart: number,
  annotationEnd: number,
  start: number,
  end: number
): boolean {
  // An annotation is considered to overlap the region if any part of it is contained within the selection,
  // which is consistent with behavior of sequence.parts().getSequencePartsBetween().
  return sequtil.isRangeOverlap(annotationStart, annotationEnd, start, end);
}

export function isDuplicateProtectedRegion(
  start: number,
  end: number,
  protectedRegions: Array<ProtectedRegion>
): boolean {
  return protectedRegions.some(
    (protectedRegion: ProtectedRegion) => protectedRegion.start === start && protectedRegion.end === end
  );
}

/**
 * Given indices in terms of the sequence (regionStart, regionEnd), returns indices in terms of the
 * optimization bases (state.bases) so that they are 0-indexed and linear.
 */
export function getRegionBounds(
  regionStart: number,
  regionEnd: number,
  optimizationStart: number,
  optimizationEnd: number,
  seqLength: number,
  strand: Strand
): Array<NumericRange> {
  const overlaps = sequtil.getRangeOverlap(regionStart, regionEnd, optimizationStart, optimizationEnd);
  const optimizationLength = sequtil.length(optimizationStart, optimizationEnd, seqLength);
  return overlaps.map((overlap) => {
    const start = sequtil.length(optimizationStart, overlap[0], seqLength) % seqLength;
    const end = sequtil.length(optimizationStart, overlap[1], seqLength);
    return {
      start: strand === 1 || strand === 0 ? start : optimizationLength - end,
      end: strand === 1 || strand === 0 ? end : optimizationLength - start,
    };
  });
}

/**
 * Given array of possibly overlapping regions (start, end), and the length of a larger range, returns the
 * complements of the regions within that range: that is, the parts of the entire range not covered by the
 * regions in the array. Regions must be 0-indexed.
 */
export function getRegionComplements(regions: Array<NumericRange>, rangeLength: number): Array<NumericRange> {
  interface RegionBoundary {
    type: 'start' | 'end';
    index: number;
  }
  const sortedBoundaries: Array<RegionBoundary> = sortedBy(
    regions.flatMap(
      (region: NumericRange): Array<RegionBoundary> => [
        {type: 'start', index: region.start},
        {type: 'end', index: region.end},
      ]
    ),
    ['index']
  );
  const resultRegions: Array<NumericRange> = [];
  let depth = 0;
  let complementRangeStart = 0;
  for (const sortedBoundary of sortedBoundaries) {
    assert(depth >= 0, 'Start and end indices are mismatched');
    if (sortedBoundary.type === 'start') {
      depth++;
      if (depth === 1) {
        resultRegions.push({start: complementRangeStart, end: sortedBoundary.index});
      }
    } else {
      depth--;
      if (depth === 0) {
        complementRangeStart = sortedBoundary.index;
      }
    }
  }
  assert(depth === 0, 'Start and end indices are mismatched');
  resultRegions.push({start: complementRangeStart, end: rangeLength});
  return resultRegions.filter((resultRegion) => resultRegion.start !== resultRegion.end);
}

export function getUniqueProtectedRegions(protectedRegions: Array<NumericRange>): Array<NumericRange> {
  const uniqueProtectedRegions: Array<NumericRange> = [];
  for (const protectedRegion of protectedRegions) {
    if (
      uniqueProtectedRegions.every(
        (uniqueRegion) =>
          uniqueRegion.start !== protectedRegion.start || uniqueRegion.end !== protectedRegion.end
      )
    ) {
      uniqueProtectedRegions.push(protectedRegion);
    }
  }
  return uniqueProtectedRegions;
}

export function checkInvalidProtectedRegion(
  currentStart: number,
  currentEnd: number | null,
  optimizationStart: number,
  optimizationEnd: number,
  protectedRegions: Array<ProtectedRegion>
): string | null {
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (currentStart === null || currentEnd === null) {
    return 'No region selected.';
  } else if (!sequtil.isRangeOverlap(currentStart, currentEnd, optimizationStart, optimizationEnd)) {
    return 'The region you have selected is not contained in the region to be optimized.';
  } else if (isDuplicateProtectedRegion(currentStart, currentEnd, protectedRegions)) {
    return 'The region you have selected is identical to an existing protected region.';
  } else if (sequtil.isRangeWithin(optimizationStart, optimizationEnd, currentStart, currentEnd)) {
    return 'The region you have selected contains the region to be optimized.';
  } else {
    return null;
  }
}

export function checkInvalidPreviewRequest(
  cutsitesToPreserve: Array<EnzymeRecognitionSite>,
  cutsitesToAvoid: Array<EnzymeRecognitionSite>,
  selectedOrganism: Organism | null,
  avoidHairpins: boolean,
  hairpinStem: HairpinParameter,
  hairpinWindow: HairpinParameter,
  uridineDepletion: boolean,
  gcLevel: GCLevel,
  patternsToReduce: Array<PatternToReduce>
): string | null {
  const overlappingEnzymes: Array<string> = [];
  cutsitesToPreserve.forEach((cutsiteToPreserve) => {
    if (cutsitesToAvoid.map((cutsiteToAvoid) => cutsiteToAvoid.id).includes(cutsiteToPreserve.id)) {
      overlappingEnzymes.push(cutsiteToPreserve.enzymeName);
    }
  });
  if (overlappingEnzymes.length > 0) {
    return `${pluralize(overlappingEnzymes.length, 'Enzyme')} ${overlappingEnzymes.join(
      ', '
    )} cannot be present in both Avoided Cut Sites and Preserved Cut Sites`;
  }
  if (hairpinStem.error.length > 0 || hairpinWindow.error.length > 0) {
    return 'Invalid hairpin parameters.';
  }
  if (
    selectedOrganism == null &&
    gcLevel === GCLevel.ANY &&
    !uridineDepletion &&
    !avoidHairpins &&
    cutsitesToAvoid.length === 0 &&
    patternsToReduce.filter((pattern) => pattern != null).length === 0
  ) {
    return 'No objectives were selected for optimization. Optimization would return no changes.';
  }
  if (_.any(patternsToReduce, (pattern) => isInvalidDNAPattern(pattern))) {
    return 'Invalid patterns to reduce.';
  }
  return null;
}

/**
 * Return an array of protected regions for a DNA sequence by accounting for additional protected regions
 * needed to preserve cut sites.
 */
export function getProtectedRegionsForSequence(
  matchSites: Array<MatchSite>,
  cutsitesToPreserve: Array<EnzymeRecognitionSite>,
  optimizationStart: number,
  optimizationEnd: number,
  seqLength: number,
  strand: Strand,
  protectedRegions: Array<ProtectedRegion>
): Array<NumericRange> {
  const matchSitesToProtect: Array<NumericRange> = matchSites
    .filter((matchSite: MatchSite) =>
      cutsitesToPreserve.some((cutsite) => cutsite.enzymeName === matchSite.enzymeName)
    )
    .flatMap((matchSite: MatchSite) =>
      getRegionBounds(
        matchSite.matchStart,
        matchSite.matchEnd,
        optimizationStart,
        optimizationEnd,
        seqLength,
        strand
      )
    );
  return getUniqueProtectedRegions([
    ...protectedRegions.flatMap((region) =>
      getRegionBounds(region.start, region.end, optimizationStart, optimizationEnd, seqLength, strand)
    ),
    ...matchSitesToProtect,
  ]);
}

/**
 * Return an array of cut sites to avoid for a DNA sequence, accounting for match sites already present in the
 * original sequence. Assumes that cutsitesToPreserve and cutsitesToAvoid have no enzymes in common, which is
 * checked by checkInvalidPreviewRequest
 */
export function getCutsitesToAvoidForSequence(
  cutsitesToPreserve: Array<EnzymeRecognitionSite>,
  cutsitesToAvoid: Array<EnzymeRecognitionSite>,
  matchSites: Array<MatchSite>,
  optimizationStart: number,
  optimizationEnd: number,
  seqLength: number,
  strand: Strand
): Array<APIEnzymeRecognitionSite> {
  return [
    // Preserved cut sites should be avoided in ranges where they don't already exist.
    ...cutsitesToPreserve.flatMap((cutsite: EnzymeRecognitionSite) =>
      getRegionComplements(
        matchSites
          .filter((matchSite: MatchSite) => matchSite.enzymeName === cutsite.enzymeName)
          .map((matchSite: MatchSite) => ({
            regionStart: matchSite.matchStart,
            regionEnd: matchSite.matchEnd,
          }))
          .flatMap((region) =>
            getRegionBounds(
              region.regionStart,
              region.regionEnd,
              optimizationStart,
              optimizationEnd,
              seqLength,
              strand
            )
          ),
        sequtil.length(optimizationStart, optimizationEnd, seqLength)
      ).map((region: NumericRange) => ({
        start: region.start,
        end: region.end,
        recognitionSiteDna: cutsite.recognitionSite,
      }))
    ),
    // Avoided cut sites should be avoided everywhere.
    ...cutsitesToAvoid.map((cutsite: EnzymeRecognitionSite) => ({
      start: 0,
      end: sequtil.length(optimizationStart, optimizationEnd, seqLength),
      recognitionSiteDna: cutsite.recognitionSite,
    })),
  ];
}

export function getOrientedBases(optimizedBases: string, strand: Strand): string {
  return strand === 1 || strand === 0
    ? optimizedBases
    : BiojsUtil.revcomp(optimizedBases, DNA_COMPLEMENT_TABLE);
}

export function getCasedBasesToSave(originalBases: string, optimizedBases: string, strand: Strand): string {
  const newBases = getOrientedBases(optimizedBases, strand);
  const casedBases = optimizedBases
    .split('')
    .map((base, i) => {
      const currentIndex = strand === 1 ? i : newBases.length - i - 1;
      return withCase(base, getLetterCase(originalBases[currentIndex]));
    })
    .join('');
  return casedBases;
}

export function isInvalidDNAPattern(patternToReduce: PatternToReduce): boolean {
  return (
    patternToReduce.pattern === '' ||
    !BiojsUtil.isSimpleDNASequence(patternToReduce.pattern) ||
    patternToReduce.pattern.length > 10
  );
}

export function getInvalidDNAText(patternToReduce: PatternToReduce): string {
  assertAndContinue(isInvalidDNAPattern(patternToReduce), 'Expected an invalid DNA pattern.');
  return patternToReduce.pattern === ''
    ? 'Please specify a DNA pattern.'
    : patternToReduce.pattern.length > MAXIMUM_PATTERN_LENGTH
      ? `Invalid DNA pattern. Pattern cannot be longer than ${MAXIMUM_PATTERN_LENGTH} bases long.`
      : !BiojsUtil.isSimpleDNASequence(patternToReduce.pattern)
        ? 'Invalid DNA pattern. Pattern must consist of ATGC bases only.'
        : '';
}

export function getApiPatternsToReduce(patternsToReduce: Array<PatternToReduce>): Array<string> {
  const apiPatterns: Array<string> = [];
  patternsToReduce.forEach((patternToReduce) => {
    apiPatterns.push(patternToReduce.pattern);
    if (patternToReduce.reverseComplement) {
      apiPatterns.push(BiojsUtil.revcomp(patternToReduce.pattern, DNA_COMPLEMENT_TABLE));
    }
  });
  return apiPatterns;
}

interface NotePartSpec {
  text?: string;
  itemId?: string;
}

export interface NotePartOptions {
  isBackTranslation: boolean;
  originalEntityId: string;
  start: number;
  end: number;
  strand: Strand | null;
  organism: Organism | null;
  gcLevel: GCLevel;
  uridineDepletion: boolean;
  avoidHairpins: boolean;
  hairpinStem: HairpinParameter | null;
  hairpinWindow: HairpinParameter | null;
  protectedRegions: Array<ProtectedRegion>;
  cutsitesToAvoid: Array<EnzymeRecognitionSite>;
  cutsitesToPreserve: Array<EnzymeRecognitionSite>;
  patternsToReduce: Array<PatternToReduce>;
  optimizationMetrics: OptimizationMetrics | null;
}

function _getHeaderNotePartsForCodonOptimizedSequence(options: NotePartOptions): Array<NotePartSpec> {
  const {isBackTranslation, originalEntityId, start, end, strand} = options;
  const noteParts = [];
  assertAndContinue(
    isBackTranslation || (strand != null && [1, -1].includes(strand)),
    'Expected sequence to have strand in (-1, 1).'
  );
  const strandText = isBackTranslation ? 'none' : strand === 1 ? 'forward' : 'reverse';
  const text = isBackTranslation
    ? 'This sequence was created by back translating and codon optimizing the following ' +
      `AA sequence from AA ${start} to AA ${end}: \n`
    : 'This sequence was created by codon optimizing the following DNA sequence from base ' +
      `${start} to base ${end} on the ${strandText} strand: \n`;
  noteParts.push({text}, {itemId: originalEntityId});
  return noteParts;
}

function _getParametersNotePartsForCodonOptimizedSequence(options: NotePartOptions): Array<NotePartSpec> {
  const {
    organism,
    gcLevel,
    uridineDepletion,
    avoidHairpins,
    hairpinStem,
    hairpinWindow,
    protectedRegions,
    cutsitesToAvoid,
    cutsitesToPreserve,
    patternsToReduce,
  } = options;
  const noteParts = [];
  if (organism) {
    noteParts.push({
      text: `The sequence was optimized for the organism ${organism}.`,
    });
  }
  if (gcLevel !== GCLevel.ANY) {
    noteParts.push({
      text: `The GC level was optimized to be ${GC_LEVEL_NAME[gcLevel]}.`,
    });
  }
  if (uridineDepletion) {
    noteParts.push({
      text: 'The sequence was optimized to contain fewer thymine bases (mRNA uridine depletion).',
    });
  }
  if (avoidHairpins) {
    if (hairpinStem != null && hairpinWindow != null) {
      noteParts.push({
        text: `Hairpins were avoided with a stem size of ${hairpinStem.value} and a window size of ${hairpinWindow.value}.`,
      });
    } else {
      assertAndContinue(false, 'Expected hairpin parameters to be specified.');
    }
  }
  if (protectedRegions.length > 0) {
    noteParts.push({
      text: 'The following regions from the original sequence were protected:',
    });
    noteParts.push(
      ...protectedRegions.map((region) => ({
        text: `${region.name}: ${region.start}-${region.end}`,
        bulleted: true,
      }))
    );
  }
  if (cutsitesToAvoid.length > 0) {
    noteParts.push({
      text: `Existing cutsites were removed and no new ones were created for the following enzymes: ${cutsitesToAvoid
        .map((cutsite) => cutsite.enzymeName)
        .join(', ')}.`,
    });
  }
  if (cutsitesToPreserve.length > 0) {
    noteParts.push({
      text: `Existing cutsites were preserved for the following enzymes: ${cutsitesToPreserve
        .map((cutsite) => cutsite.enzymeName)
        .join(', ')}.`,
    });
  }
  if (patternsToReduce.length > 0) {
    noteParts.push({
      text: `The following DNA patterns were minimized: ${getApiPatternsToReduce(patternsToReduce).join(
        ', '
      )}.`,
    });
  }
  return noteParts;
}

function getChangeVerb(originalMetric: number, optimizedMetric: number): string {
  if (originalMetric > optimizedMetric) {
    return 'decreased';
  } else {
    return 'increased';
  }
}
function getNotePartTextForMetric(
  metricName: string,
  optimizedMetric: number,
  originalMetric: number,
  isBackTranslation: boolean
): string {
  if (!isBackTranslation && originalMetric !== optimizedMetric) {
    return `The ${metricName} was ${getChangeVerb(
      originalMetric,
      optimizedMetric
    )} from ${originalMetric} to ${optimizedMetric}.`;
  } else if (isBackTranslation) {
    return `The optimized ${metricName} was ${optimizedMetric}.`;
  } else {
    return `The ${metricName} was not changed.`;
  }
}
function roundIfFloat(num: number): number {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

function _getMetricsNotePartsForCodonOptimizedSequence(options: NotePartOptions): Array<NotePartSpec> {
  const {isBackTranslation, optimizationMetrics} = options;
  const noteParts = [];
  if (optimizationMetrics) {
    noteParts.push({text: 'Optimization Results:'});
    if (!isBackTranslation) {
      noteParts.push({
        text: `${optimizationMetrics.numCodonsChanged} ${pluralize(
          optimizationMetrics.numCodonsChanged,
          'codon'
        )} ${optimizationMetrics.numCodonsChanged === 1 ? 'was' : 'were'} changed.`,
      });
    }
    const metricKeyToMetricName: {[k in MetricKeys]: string} = {
      numRareCodons: 'number of rare codons',
      gcContent: 'GC content',
      thymineContent: 'thymine content',
      numHairpins: 'number of hairpins',
    };
    for (const metricKey of Object.keys(metricKeyToMetricName)) {
      if (optimizationMetrics[metricKey as MetricKeys]) {
        noteParts.push({
          text: getNotePartTextForMetric(
            metricKeyToMetricName[metricKey as MetricKeys],
            roundIfFloat(optimizationMetrics[metricKey as MetricKeys]!.optimized),
            roundIfFloat(optimizationMetrics[metricKey as MetricKeys]!.original),
            isBackTranslation
          ),
        });
      }
    }
    for (const patternChange of optimizationMetrics.patterns) {
      noteParts.push({
        text: getNotePartTextForMetric(
          `number of occurrences of pattern ${patternChange.pattern}`,
          roundIfFloat(patternChange.optimized),
          roundIfFloat(patternChange.original),
          isBackTranslation
        ),
      });
    }
  }
  return noteParts;
}

// Keep in sync with update_codon_optimized_sequence_description on backend.
export function getNotePartsForCodonOptimizedSequence(options: NotePartOptions): Array<NotePartSpec> {
  return [
    ..._getHeaderNotePartsForCodonOptimizedSequence(options),
    ..._getParametersNotePartsForCodonOptimizedSequence(options),
    ..._getMetricsNotePartsForCodonOptimizedSequence(options),
  ];
}
